import View from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import Scroll from 'src/game/components/shared/Scroll';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  buddiesGetEvent,
  buddiesIsEventCompleted,
  buddiesIsPartyFull,
} from 'src/replicant/buddies/buddies.getters';
import { BuddiesNoFriends } from './BuddiesNoFriends';
import {
  buddiesGetFriends,
  buddiesGetSuggestedPlayers,
  removeBuddiesSuggestionFromCache,
} from '../buddies.friends';
import sounds from 'src/lib/sounds';
import type {
  BuddiesPlayerMap,
  BuddiesSuggestedPlayer,
} from 'src/replicant/buddies/buddies.asyncGetters';
import type { BuddiesEvent } from 'src/replicant/buddies/buddies.schema';
import {
  BuddiesFriendEntry,
  BuddiesInviteItemParams,
} from './BuddiesFriendEntry';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { track } from 'src/lib/analytics/utils';
import { buddiesGetCommonProps } from 'src/replicant/buddies/buddies.getters';
import { buddiesSuggestionsEnabled } from 'src/replicant/buddies/buddies.ruleset';

type BuddiesTabFriendsOpts = {
  tab: View;
  onRefresh: () => Promise<void>;
};

export class BuddiesTabFriends {
  private tab: View;
  private scroll: Scroll<BuddiesInviteItemParams, BuddiesFriendEntry>;
  private noFriends: BuddiesNoFriends;
  private partyFullContainer: View;
  private suggestions: BuddiesSuggestedPlayer[] = [];
  private loadingSuggestions = true;
  private onRefresh: BuddiesTabFriendsOpts['onRefresh'];

  constructor(opts: BuddiesTabFriendsOpts) {
    this.tab = opts.tab;
    this.onRefresh = opts.onRefresh;

    const scrollRect = {
      x: 0,
      y: 42,
      width: this.tab.style.width,
      height: this.tab.style.height - 23,
    };

    this.scroll = new Scroll({
      superview: this.tab,
      createItem: (
        superview: View,
        _i: number,
        data: BuddiesInviteItemParams,
      ) => {
        const entry = new BuddiesFriendEntry({ superview, x: 20 });
        entry.setup({ ...data });
        return entry;
      },
      recycle: false,
      rect: scrollRect,
    });

    this.noFriends = new BuddiesNoFriends({
      superview: this.tab,
      x: this.tab.style.width / 2,
      y: this.tab.style.height / 2,
      visible: false,
      centerOnOrigin: true,
    });

    this.partyFullContainer = new View({
      superview: this.tab,
      width: this.tab.style.width,
      height: this.tab.style.height,
      visible: false,
    });

    new LangBitmapFontTextView({
      superview: this.partyFullContainer,
      x: this.partyFullContainer.style.width / 2,
      y: this.partyFullContainer.style.height * 0.3,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 28,
      font: 'Body',
      width: this.partyFullContainer.style.width - 60,
      height: 100,
      color: '#9C693A',
      localeText: () => i18n('match2.buddies.invitePopup.partyFullFriends'),
    });

    new ImageScaleView({
      superview: this.partyFullContainer,
      image: 'assets/ui/buddies/popupInvite/full_party.png',
      scaleMethod: 'contain',
      width: 300,
      height: 300,
      x: this.partyFullContainer.style.width / 2,
      y: this.partyFullContainer.style.height * 0.6,
      centerOnOrigin: true,
    });
  }

  public async refresh() {
    const friendsMap = await buddiesGetFriends();
    const event = buddiesGetEvent(user(), now());
    const partyFull = buddiesIsPartyFull(user(), now());
    const eventCompleted = buddiesIsEventCompleted(user(), now());

    const entries: BuddiesInviteItemParams[] = [];

    if (!partyFull && !eventCompleted) {
      this.buildFriendsSection(entries, friendsMap, event);
      this.buildSuggestionsSection(entries);
    }

    this.updateVisibility(entries, partyFull);
    this.scroll.setItems(entries, false);
  }

  private buildFriendsSection(
    entries: BuddiesInviteItemParams[],
    friendsMap: BuddiesPlayerMap,
    event: BuddiesEvent | undefined,
  ) {
    const partyIds = new Set(Object.keys(event?.party ?? {}));
    const inviteIds = new Set(Object.keys(event?.invites ?? {}));
    const invitableFriends = Object.values(friendsMap).filter(
      (f) => !partyIds.has(f.id) && !inviteIds.has(f.id),
    );

    if (invitableFriends.length === 0) return;

    entries.push({ title: i18n('match2.buddies.invitePopup.yourFriends') });
    for (const friend of invitableFriends) {
      entries.push({
        id: friend.id,
        name: friend.name,
        photo: friend.photo,
        status: 'not-invited',
        onAction: async () => {
          sounds.sfx.play('ok');
          await this.sendInvite(friend.id);
        },
      });
    }
  }

  private buildSuggestionsSection(entries: BuddiesInviteItemParams[]) {
    // If suggestions are disabled, just skip
    if (!buddiesSuggestionsEnabled) return;

    if (this.suggestions.length > 0) {
      entries.push({
        title: i18n('match2.buddies.invitePopup.suggestions'),
        message: i18n('match2.buddies.invitePopup.suggestionsDisclosure'),
      });
      for (const suggestion of this.suggestions) {
        entries.push({
          id: suggestion.id,
          name: suggestion.name,
          photo: suggestion.photo,
          status: 'suggestion',
          onAction: async () => {
            sounds.sfx.play('ok');
            await this.sendSuggestionInvite(suggestion);
          },
          onSecondaryAction: async () => {
            sounds.sfx.play('ok');
            await this.dismissSuggestion(suggestion.id);
          },
        });
      }
    } else if (this.loadingSuggestions) {
      entries.push({
        title: i18n('match2.buddies.invitePopup.suggestions'),
        message: i18n('match2.buddies.invitePopup.loading'),
      });
    }
  }

  private updateVisibility(
    entries: BuddiesInviteItemParams[],
    partyFull: boolean,
  ) {
    if (partyFull) {
      this.partyFullContainer.show();
      this.noFriends.hide();
      this.scroll.getView().hide();
    } else if (entries.length === 0) {
      this.noFriends.show();
      this.partyFullContainer.hide();
      this.scroll.getView().hide();
    } else {
      this.noFriends.hide();
      this.partyFullContainer.hide();
      this.scroll.getView().show();
    }
  }

  public async fetchSuggestions() {
    if (!buddiesSuggestionsEnabled) {
      this.loadingSuggestions = false;
      return;
    }
    try {
      this.suggestions = await buddiesGetSuggestedPlayers().get();
    } catch {
      this.suggestions = [];
    }
    this.loadingSuggestions = false;
  }

  private async sendInvite(receiverId: string) {
    await withLoading(
      async () => {
        await StateObserver.invoke.buddiesSendInvite({ receiverId });
        track('BuddiesRequestSent', {
          ...buddiesGetCommonProps(user(), now()),
          inviteMethod: 'friend',
          recipientId: receiverId,
        });
        await this.onRefresh();
      },
      {
        feature: Feature.BUDDIES._,
        subFeature: Feature.BUDDIES.TAB_FRIENDS,
        superview: this.tab,
      },
    );
  }

  private async sendSuggestionInvite(suggestion: BuddiesSuggestedPlayer) {
    await withLoading(
      async () => {
        await StateObserver.invoke.buddiesSendInvite({
          receiverId: suggestion.id,
        });
        track('BuddiesRequestSent', {
          ...buddiesGetCommonProps(user(), now()),
          inviteMethod: 'friend',
          recipientId: suggestion.id,
        });
        this.suggestions = this.suggestions.filter(
          (s) => s.id !== suggestion.id,
        );
        removeBuddiesSuggestionFromCache(suggestion.id);
        if (this.suggestions.length === 0) {
          await this.fetchSuggestions();
        }
        await this.onRefresh();
      },
      {
        feature: Feature.BUDDIES._,
        subFeature: Feature.BUDDIES.TAB_FRIENDS,
        superview: this.tab,
      },
    );
  }

  private async dismissSuggestion(playerId: string) {
    await withLoading(
      async () => {
        await StateObserver.invoke.buddiesRejectSuggestion({ playerId });
        this.suggestions = this.suggestions.filter((s) => s.id !== playerId);
        removeBuddiesSuggestionFromCache(playerId);
        if (this.suggestions.length === 0) {
          await this.fetchSuggestions();
        }
        await this.onRefresh();
      },
      {
        feature: Feature.BUDDIES._,
        subFeature: Feature.BUDDIES.TAB_FRIENDS,
        superview: this.tab,
      },
    );
  }
}
